/**
 * PasswordStrengthIndicator
 * =============================================================================
 * Live-updating strength meter + per-rule checklist below a password input.
 *
 * Renders nothing when the password is empty so the form layout doesn't jump
 * before the user starts typing.
 *
 * Usage:
 *   <input value={pw} onChange={...} />
 *   <PasswordStrengthIndicator password={pw} />
 */

import { useTranslation } from 'react-i18next';
import {
  checkPasswordStrength,
  PASSWORD_MIN_LENGTH,
  type PasswordRuleId,
} from '../utils/passwordStrength';

interface Props {
  password: string;
}

// Index 0..4 -> Tailwind bg color for the bar. Red -> green progression.
const LEVEL_BAR_COLORS = [
  'bg-red-500',
  'bg-orange-500',
  'bg-yellow-500',
  'bg-blue-500',
  'bg-green-500',
] as const;

const LEVEL_TEXT_COLORS = [
  'text-red-600',
  'text-orange-600',
  'text-yellow-700',
  'text-blue-600',
  'text-green-600',
] as const;

const LEVEL_LABEL_KEYS = [
  'veryWeak',
  'weak',
  'fair',
  'good',
  'strong',
] as const;

const RULE_ORDER: PasswordRuleId[] = [
  'minLength',
  'lowercase',
  'uppercase',
  'number',
  'special',
];

export default function PasswordStrengthIndicator({ password }: Props) {
  const { t } = useTranslation();

  if (!password) {
    return null;
  }

  const { checks, level, meetsAllRequirements } = checkPasswordStrength(password);
  const labelKey = LEVEL_LABEL_KEYS[level];
  const barColor = LEVEL_BAR_COLORS[level];
  const textColor = meetsAllRequirements
    ? 'text-green-700 font-medium'
    : LEVEL_TEXT_COLORS[level];

  // Sort checks into a stable display order rather than the order they happen
  // to be in the result.
  const checksByOrder = RULE_ORDER.map(
    (id) => checks.find((c) => c.id === id) ?? { id, passed: false },
  );

  return (
    <div className="mt-2 space-y-2" data-testid="password-strength">
      {/* Five-segment bar - segments at or below level get colored. */}
      <div className="flex gap-1">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className={`h-1.5 flex-1 rounded-full transition-colors ${
              i <= level ? barColor : 'bg-gray-200'
            }`}
          />
        ))}
      </div>

      <div className={`text-xs ${textColor}`}>
        {t(`passwordStrength.${labelKey}`)}
      </div>

      <ul className="space-y-1 text-xs">
        {checksByOrder.map(({ id, passed }) => (
          <li
            key={id}
            className={`flex items-center gap-2 ${
              passed ? 'text-green-700' : 'text-gray-500'
            }`}
          >
            {passed ? (
              <svg
                className="w-3.5 h-3.5 flex-shrink-0 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={3}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            ) : (
              <svg
                className="w-3.5 h-3.5 flex-shrink-0 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <circle cx="12" cy="12" r="4" strokeWidth={2} />
              </svg>
            )}
            <span>
              {id === 'minLength'
                ? t('passwordStrength.requirement.minLength', { count: PASSWORD_MIN_LENGTH })
                : t(`passwordStrength.requirement.${id}`)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
